/**
 * Pure formatters used across the University surface — no React, easy
 * to unit-test, no side effects.
 */

export function fmtScore(score: number | null | undefined): string {
  if (score == null || !Number.isFinite(score)) return '—';
  return `${(score * 100).toFixed(0)}%`;
}

export function fmtCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(value);
}

export function fmtRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '—';
  const diffMs = now.getTime() - ts;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr}y ago`;
}

/**
 * Map an effectiveness score (0..1) to a status semantic colour.
 *
 *   ≥ 0.8 → success (green)
 *   ≥ 0.5 → warning (yellow)
 *   < 0.5 → error   (red)
 *   null → muted
 */
export type EffectivenessTone = 'success' | 'warning' | 'error' | 'muted';

export function effectivenessTone(score: number | null | undefined): EffectivenessTone {
  if (score == null || !Number.isFinite(score)) return 'muted';
  if (score >= 0.8) return 'success';
  if (score >= 0.5) return 'warning';
  return 'error';
}

export const TONE_DOT_CLASS: Record<EffectivenessTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-destructive',
  muted: 'bg-muted-foreground/40',
};

export const TONE_TEXT_CLASS: Record<EffectivenessTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-destructive',
  muted: 'text-muted-foreground',
};

/** Friendly label for marketplace status. */
export const MARKETPLACE_STATUS_LABEL: Record<string, string> = {
  private: 'Private',
  candidate: 'Candidate',
  exportable: 'Exportable',
  published: 'Published',
  sold: 'Sold',
  archived: 'Archived',
};

/** Friendly label for training-session status. */
export const SESSION_STATUS_LABEL: Record<string, string> = {
  planned: 'Planned',
  active: 'Active',
  awaiting_tuner: 'Awaiting tuner',
  completed: 'Completed',
  abandoned: 'Abandoned',
};

export function sessionStatusTone(status: string): EffectivenessTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'active':
    case 'awaiting_tuner':
      return 'warning';
    case 'abandoned':
      return 'error';
    default:
      return 'muted';
  }
}
